<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getLeadsAction } from '@/api/functions/leads/get-leads-action'
import type { LeadItem } from '@/api/typings/lead.typing.ts'
import type { ApiPaginationResponse } from '@/api/typings/response.typing'
import LeadCard from './partials/LeadCard.vue'

const router = useRouter()
const route = useRoute()

const currentPage = ref(1)
const activeFilters = ref<Record<string, any>>({})

// Fetch Leads
const { data: leadsData, isLoading } = useQuery<ApiPaginationResponse<LeadItem[]>>({
  queryKey: computed(() => ['leads', currentPage.value, activeFilters.value]),
  queryFn: () => {
    const filters = Object.fromEntries(
      Object.entries(activeFilters.value).filter(([key, value]) => key !== 'page' && value !== false)
    )

    return getLeadsAction({
      page: currentPage.value,
      ...filters,
    })
  },
  // keepPreviousData: true,

  staleTime: 1000 * 60 * 3,
})

//  Filter Fields
const filterFields = [
  {
    type: 'input',
    key: 'full_name',
    label: 'نام کاربر را وارد کنید',
    title: 'جستجو',
  },
  {
    type: 'date',
    key: 'requestDate',
    title: 'تاریخ ثبت',
    rangeKeys: {
      min: 'from_date',
      max: 'till_date',
    },
  },
]

const onApplyFilters = (values: Record<string, any>) => {
  activeFilters.value = { ...values }
  currentPage.value = 1

  const query = Object.fromEntries(Object.entries(values).filter(([_, v]) => v))
  router.push({ path: route.path, query })
}

const onResetFilters = () => {
  activeFilters.value = {}
  currentPage.value = 1
  router.push({ path: route.path, query: {} })
}

const leads = computed(() => leadsData.value?.data || [])
const pagination = computed(() => leadsData.value?.pagination)
const totalItems = computed(() => pagination.value?.total || 0)

const goToPage = (page: number) => {
  currentPage.value = page
}

onMounted(() => {
  if (Object.keys(route.query).length > 0) {
    const { page, ...filters } = route.query

    activeFilters.value = filters

    if (page) {
      currentPage.value = Number(page)
    }
  }
})
</script>

<template>
  <div class="w-full mb-12">
    <h2 class="text-[22px] text-[#1b325a ] mb-7">لیست لیدها</h2>

    <!-- 👉 Filters Section -->
    <Filters  :fields="filterFields" @apply="onApplyFilters" @reset="onResetFilters"
      class="mt-6" />

    <!-- 👉 Lead list and pagination -->
    <div v-if="isLoading" class="text-center py-8 text-gray-500">
      در حال دریافت اطلاعات...
    </div>

    <div v-else>
      <div v-if="leads.length > 0" class="mt-8 flex flex-col gap-6">
        <LeadCard v-for="(lead, index) in leads" :key="`card-item-${index}`" :lead="lead" />

        <Pagination v-model:currentPage="currentPage" :itemsPerPage="pagination?.perPage" :totalItems="totalItems"
          @update:currentPage="goToPage" :isAdmin="true" />
      </div>

      <EmptyState v-else :onReset="onResetFilters" />
    </div>
  </div>
</template>
